import * as PIXI from 'pixi.js';
import * as PIXIFilters from 'pixi-filters';
import { ProcessSpectrum, GetSpectrumMagnitude } from '../../ProcessSpectrum';
import { CONSTANTS, WAVE_TYPE, WAVE_STYLE, REFLECTION_TYPE, GLOW_TYPE } from '../../PreviewDisplayConstants';
import DriftEffect from '../../effects/drift/DriftEffect';
import { getEffectiveDrift, VISUALIZER_ANCHORS } from '../../effects/drift/driftUtilities';
import chroma from 'chroma-js';
import { average } from '../../../../../../utilities/mathUtilities';

const baseHeight = 500;
const fireSpeed = 4;

// The ERASE blend only reads the shape's alpha, so the knockout fill colour is arbitrary.
const CUTOUT_ERASE_COLOR = 0xFFFFFF;

export class FlatWave {
    constructor(stage, zIndex) {
        this.stage = stage;

        this.waveSettings = [];
        this.x = baseHeight * 0.5;
        this.y = baseHeight * 0.5;
        this.offset = { x: 0, y: 0 };
        this.driftEffect = new DriftEffect();

        this.container = new PIXI.Container();
        this.stage.addChild(this.container);
        this.container.zIndex = zIndex;

        // "Transparent" layers knock out the visualizer with an ERASE blend to reveal the background.
        // Each layer owns its own ERASE graphic at the layer's z-position (see addNewLayer), so a cutout
        // subtracts only the layers drawn BEHIND it; layers in front draw on top and stay visible. ERASE
        // only stays inside the visualizer because the container is isolated by cutoutIsolationFilter
        // while any cutout is active (otherwise ERASE would also erase the already-drawn background).
        this.cutoutIsolationFilter = new PIXI.filters.AlphaFilter(1);

        // The layer stack needs an isolating framebuffer of its OWN, not just the shared one above.
        // Without it a cutout's ERASE reaches past the layers and wipes the glow container underneath.
        this.layerStackIsolationFilter = new PIXI.filters.AlphaFilter(1);

        // Content containers for each layer will be added here
        this.layerEffects = [];
        this.config = {};
    }

    update(config) {
        this.config = config;

        // Initiation layers
        if (!this.waveCircles)
            this.initializeLayer(config.waveCircles);

        this.waveSettings = config.waveCircles;

        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;

        this.setPositionAndScale(multiplier);
        this.updateLayerEffects(multiplier);

        this.baseRotation = this.config.rotation / (180 / Math.PI);
        this.container.rotation = this.baseRotation;

        // Store config for drift
        if (this.config.drift) {
            const effectiveDrift = getEffectiveDrift(this.config.drift, VISUALIZER_ANCHORS);
            this.driftEffect.updateConfig(effectiveDrift);

            this.driftEffect.setBaseTransform(
                { x: this.x, y: this.y },
                this.baseRotation,
                { x: this.baseScaleMultiplier, y: this.baseScaleMultiplier }
            );
            this.driftEffect.setDimensions(
                this.config.width * this.baseScaleMultiplier,
                baseHeight * this.baseScaleMultiplier
            );
        }
    }

    updateLayers(waveCircles) {
        this.initializeLayer(waveCircles);
        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;
        this.updateLayerEffects(multiplier);
    }

    getProcessedSpectrum(spectrums, waveType, frameDelay, spectrumOptions) {
        const targetSpectrum = spectrums[waveType === WAVE_TYPE.BASS_SPECTRUM ? "bass" : "wide"];
        return ProcessSpectrum(targetSpectrum[frameDelay], spectrumOptions);
    }

    /**
     * Main function to update the transform
     */
    updateTransform(spectrumMagnitude, bassSpectrumMagnitude, waveType) {
        if (this.driftEffect.enabled) {
            this.applyDriftEffect(bassSpectrumMagnitude);
        } else if (this.config.shakeEnabled) {
            this.applyShakeEffect(spectrumMagnitude, waveType);
        } else {
            // no effect is active — just apply base values
            this.applyTransform(0, 0, this.baseScaleMultiplier, this.baseRotation);
        }
    }

    /**
     * Shared function to apply transform values
     */
    applyTransform(x, y, scale, rotation) {
        this.container.x = this.x + x;
        this.container.y = this.y + y;
        this.container.scale.set(scale, scale);
        this.container.rotation = rotation;
    }

    /**
     * Shake effect
     */
    applyShakeEffect(spectrumMagnitude, waveType) {
        const shakeFactor = waveType === WAVE_TYPE.BASS_SPECTRUM ? this.config.bassShakeFactor : this.config.wideShakeFactor;

        if (this.config.shakeEnabled) {
            const newXOffset = (this.offset.x + (Math.random() - 0.5) * shakeFactor) * (spectrumMagnitude / 255);
            const newYOffset = (this.offset.y + (Math.random() - 0.5) * shakeFactor) * (spectrumMagnitude / 255);

            this.offset = { x: newXOffset, y: newYOffset };
            this.container.x = this.x + newXOffset;
            this.container.y = this.y + newYOffset;
        }
    }

    /**
     * Drift effect
     */
    applyDriftEffect(spectrumMagnitude) {
        const transform = this.driftEffect.compute(spectrumMagnitude);

        if (!transform) return;

        this.applyTransform(
            transform.translateX,
            transform.translateY,
            this.baseScaleMultiplier * transform.scale,
            this.baseRotation + transform.rotation
        );
    }

    applyGlowEffect(waveCircle, effects, layerGlowSettings, settings) {
        const glowContainer = effects.glowContainer;
        const useCustom = settings.customOptions && settings.customOptions.enabled;
        const hasCustomGlow = useCustom && layerGlowSettings && layerGlowSettings.enabled;
        const hasCustomFire = useCustom && settings.customOptions?.fireSettings && settings.customOptions.fireSettings.enabled;
        const hasCustomShadow = useCustom && settings.customOptions?.dropShadowSettings && settings.customOptions.dropShadowSettings.enabled;

        if (hasCustomGlow || (this.config.glowSettings.enabled && (hasCustomFire || hasCustomShadow))) {
            // PER-LAYER GLOW:
            // Use the per-layer glowContainer when:
            // 1. The layer has an explicit custom glow, OR
            // 2. Global glow is active but the layer has custom fire or shadow.
            //    In case (2), putting the clone in the sharedGlowContainer would render
            //    without fire/shadow distortion (shared fire/shadow only activates for global),
            //    creating a visible "ghost" layer underneath. By routing to the per-layer
            //    glowContainer, the clone inherits the layer's own displacement filter.
            const clone = waveCircle.clone();
            clone.rotation = this.getLayerRotation(settings);
            clone.scale.set(waveCircle.scale.x, waveCircle.scale.y);
            glowContainer.addChild(clone);
        }
        
        // GLOBAL GLOW:
        // Use the shared glowContainer that sits behind ALL layers.
        // This ensures the single global bloom filter processes all layer clones together,
        // creating a unified outer glow effect just like in the original 'develop' branch.
        // The shared source feeds the global shadow too, and an empty source has nothing to cast one
        // from, so a global shadow fills it even when the global glow is off.
        const avoidsGhostLayer = !(hasCustomFire || hasCustomShadow);
        const feedsSharedSource = this.config.glowSettings.enabled || this.config.dropShadowSettings.enabled;
        if (feedsSharedSource && avoidsGhostLayer) {
            const clone = waveCircle.clone();
            clone.rotation = this.getLayerRotation(settings);
            clone.scale.set(waveCircle.scale.x, waveCircle.scale.y);
            effects.glowWaveHolder.addChild(clone);
        }

        // CUTOUT GLOW BLEED:
        // The bleed pass blooms the same shapes a second time, so it needs its own copies - a clone can
        // only have one parent. Layers lit by their own custom glow feed it just like globally lit ones,
        // because the bleed represents the light spilling into the hole from whatever surrounds it. A
        // layer that does not glow is left out: it has no light to spill, and including it would wash
        // the hole with a bloom that has no source on screen.
        if (this.cutoutGlowBleedActive && this.feedsCutoutGlowBleed(settings)) {
            const bleedClone = waveCircle.clone();
            bleedClone.rotation = this.getLayerRotation(settings);
            bleedClone.scale.set(waveCircle.scale.x, waveCircle.scale.y);
            this.cutoutGlowBleedContainer.addChild(bleedClone);
        }
    }

    draw(spectrums, bassSpectrums) {
        // Note: the `spectrums` object contains arrays of historical frequency data:
        // spectrums.bass: An array for bass layers (typically 7 elements: current + 6 previous)
        // spectrums.wide: An array for "wide" layers (typically 7 elements: current + 6 previous)

        // Guard: skip drawing if PIXI objects are out of sync with settings (e.g. after layer deletion)
        if (!this.waveCircles || this.waveCircles.length !== this.waveSettings.length)
            return;

        let spectrumMagnitude = 0;
        let lastWaveType = undefined;
        const lastIndex = this.waveCircles.length - 1;

        // Rebuilt this frame from every cutout layer; toggles the isolating filter below.
        let hasCutout = false;

        // Clear shared glow container before drawing new frame
        this.layerEffects.forEach((effects) => effects.glowWaveHolder.removeChildren());

        this.prepareCutoutGlowBleed();

        this.waveCircles.forEach((waveCircle, index) => {
            const settings = this.waveSettings[index];
            const effects = this.layerEffects[index];

            // Clear per-layer glow effects before drawing new ones
            effects.glowContainer.removeChildren();

            // Clear this layer's ERASE graphic each frame so a toggled-off cutout leaves no stale hole.
            effects.cutoutEraseTargets.forEach((eraseGraphic) => eraseGraphic.clear());
            const {
                waveStyle,
                waveType,
                pointRadius,
                barWidth,
                inverted,
                reflection,
                cutout
            } = this.getVisualizationOptions(settings);

            // Calculate the processed spectrum based on settings
            const processedSpectrum = this.getProcessedSpectrum(
                spectrums,
                waveType,
                settings.frameDelay,
                settings.spectrumOptions
            );

            // Capture magnitude and type from the last circle for effects
            if (index === lastIndex) {
                spectrumMagnitude = GetSpectrumMagnitude(processedSpectrum);
                lastWaveType = waveType;
            }

            if (!settings.visible) {
                waveCircle.clear();
                return;
            }

            const targetMax = waveType === WAVE_TYPE.BASS_SPECTRUM
                ? CONSTANTS.BASS_TARGET_MAX
                : CONSTANTS.WIDE_TARGET_MAX;

            // Line reuses the Solid geometry but is stroked with no fill, so it shows only the outline
            // of the flat shape (thickness = Outline Width). A filled Solid and an outline-only Line are
            // therefore visually distinct. Mirrors the circular Line style.
            if (waveStyle === WAVE_STYLE.SOLID || waveStyle === WAVE_STYLE.LINE) {
                const { points, magnitudePercents } = this.spectrumToPoints(
                    processedSpectrum,
                    this.config.width,
                    this.config.baseHeight + (settings.heightAdjust),
                    reflection,
                    inverted,
                    settings.waveScale,
                    targetMax,
                    WAVE_STYLE.SOLID
                );
                if (cutout) {
                    if (waveStyle === WAVE_STYLE.LINE)
                        this.drawCutoutEraseLine(effects.cutoutEraseTargets, waveCircle, settings, points);
                    else
                        this.drawCutoutErase(effects.cutoutEraseTargets, waveCircle, settings, points);
                    hasCutout = true;
                } else {
                    this.drawWaveCircle(waveCircle, settings, points, magnitudePercents, waveStyle === WAVE_STYLE.LINE);
                }
            } else if (waveStyle === WAVE_STYLE.BAR) {
                const { pointSets, magnitudePercents } = this.spectrumToBarPoints(
                    processedSpectrum,
                    this.config.width,
                    this.config.baseHeight + (settings.heightAdjust),
                    reflection,
                    inverted,
                    settings.waveScale,
                    barWidth,
                    targetMax
                );
                if (cutout) {
                    this.drawCutoutEraseBars(effects.cutoutEraseTargets, waveCircle, settings, pointSets);
                    hasCutout = true;
                } else {
                    this.drawWaveCircleBars(waveCircle, settings, pointSets, magnitudePercents);
                }
            } else if (waveStyle === WAVE_STYLE.POINT) {
                const { points, magnitudePercents } = this.spectrumToPoints(
                    processedSpectrum,
                    this.config.width,
                    this.config.baseHeight + (settings.heightAdjust),
                    reflection,
                    inverted,
                    settings.waveScale,
                    targetMax,
                    waveStyle
                );
                if (cutout) {
                    this.drawCutoutErasePoints(effects.cutoutEraseTargets, waveCircle, settings, points, pointRadius);
                    hasCutout = true;
                } else {
                    this.drawWaveCirclePoints(waveCircle, settings, points, pointRadius, magnitudePercents);
                }
            }
            // A cutout layer is invisible (only a knockout hole), so it contributes no glow.
            if (!cutout)
                this.applyGlowEffect(waveCircle, effects, settings.customOptions?.glowSettings, settings);
        });

        // Isolate the container only while a cutout is active, so the ERASE blend knocks out the
        // visualizer (revealing the background) instead of erasing the already-drawn scene behind it.
        const isolated = this.container.filters != null;
        if (hasCutout && !isolated)
            this.container.filters = [this.cutoutIsolationFilter];
        else if (!hasCutout && isolated)
            this.container.filters = null;

        // The layer stack gets its own framebuffer on the same condition, so a knockout stops at the
        // bottom of the stack and leaves the glow beneath it intact.
        const stackIsolated = this.layerStackContainer.filters != null;
        if (hasCutout && !stackIsolated)
            this.layerStackContainer.filters = [this.layerStackIsolationFilter];
        else if (!hasCutout && stackIsolated)
            this.layerStackContainer.filters = null;

        this.finishCutoutGlowBleed();

        // Apply final effects after all circles are processed
        // Use bass magnitude for drift so Scale/Acceleration are perceptible.
        const bassSpectrumMagnitude = GetSpectrumMagnitude(bassSpectrums);
        this.updateTransform(spectrumMagnitude, bassSpectrumMagnitude, lastWaveType);

        // Update per-layer fire animation
        this.layerEffects.forEach((effects, index) => {
            const settings = this.waveSettings[index];
            const useCustom = settings.customOptions && settings.customOptions.enabled;
            const fire = useCustom && settings.customOptions?.fireSettings && settings.customOptions.fireSettings.enabled ? settings.customOptions.fireSettings : this.config.fireSettings;
            if (fire && fire.enabled && effects.displacementSprite) {
                effects.displacementSprite.y -= fireSpeed;
            }
        });

        // Update shared fire animation for global glow
        if (this.sharedDisplacementSprite && this.config.fireSettings.enabled) {
            this.sharedDisplacementSprite.y -= fireSpeed;
        }
    }

    // Shared preamble of every cutout style: the layer paints nothing visible. The knockout shapes are
    // written in container coordinates rather than the layer's own, because the cutout glow bleed paints
    // the very same shapes into two container-level graphics; returns the transform to apply per shape.
    prepareCutoutErase(waveGraphic, settings) {
        waveGraphic.clear();

        return { layerRotation: this.getLayerRotation(settings), layerScale: settings.scale };
    }

    // The erase, bleed and mask graphics all sit at the container origin without a layer's rotation or
    // scale, so a layer's knockout shape has to be pre-transformed (scale, then rotation) to land at the
    // same on-screen position as the wave it replaces.
    transformLayerPoints(points, rotation, scale) {
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        const transformedPoints = [];

        for (let i = 0; i < points.length; i += 2) {
            const x = points[i] * scale;
            const y = points[i + 1] * scale;
            transformedPoints.push(x * cos - y * sin);
            transformedPoints.push(x * sin + y * cos);
        }

        return transformedPoints;
    }

    // Paints one knockout shape into every graphic that has to know about it: the layer's own ERASE in
    // the layer stack, and the matching ERASE in the layer's glow slot. While the bleed pass is running
    // for a per-layer glow it needs the same shape too, in its own source and in the mask that keeps it
    // inside the hole.
    paintCutoutShape(eraseTargets, paintShape) {
        eraseTargets.forEach(paintShape);

        if (!this.cutoutGlowBleedActive)
            return;

        paintShape(this.cutoutGlowEraseGraphic);
        paintShape(this.cutoutGlowMaskGraphic);
    }

    // A cutout ("Transparent") layer draws nothing visible; the shape it would have drawn is written
    // into the layer's own ERASE graphic, which sits at the layer's z-position and knocks out the
    // layers BEHIND it, exposing the background there. The caller clears it once per frame.
    //
    // Solid knocks out its filled shape; Bar knocks out each bar; Point knocks out each dot; Line knocks
    // out only its stroke. Every style must have a knockout of its own, otherwise enabling Transparent
    // leaves the wave fully drawn.
    drawCutoutErase(eraseTargets, waveGraphic, settings, points) {
        const { layerRotation, layerScale } = this.prepareCutoutErase(waveGraphic, settings);
        const erasePoints = this.transformLayerPoints(points, layerRotation, layerScale);

        this.paintCutoutShape(eraseTargets, (graphic) => {
            graphic.beginFill(CUTOUT_ERASE_COLOR, 1);
            graphic.drawPolygon(erasePoints);
            graphic.endFill();
        });
    }

    // A Line layer paints nothing but its outline, so its knockout is that same stroke. Filling the
    // polygon instead would punch out the whole area the outline encloses - the wave and the baseline
    // it closes on would knock out everything between them.
    drawCutoutEraseLine(eraseTargets, waveGraphic, settings, points) {
        const { layerRotation, layerScale } = this.prepareCutoutErase(waveGraphic, settings);
        const strokePoints = this.transformLayerPoints(points, layerRotation, layerScale);
        // The knockout graphics carry no layer scale, so the stroke width has to be scaled by hand to
        // match the outline it replaces.
        const strokeWidth = settings.lineWidth * layerScale;

        this.paintCutoutShape(eraseTargets, (graphic) => {
            graphic.lineStyle(strokeWidth, CUTOUT_ERASE_COLOR, 1);
            graphic.drawPolygon(strokePoints);
            graphic.lineStyle(0);
        });
    }

    drawCutoutEraseBars(eraseTargets, waveGraphic, settings, pointSets) {
        const { layerRotation, layerScale } = this.prepareCutoutErase(waveGraphic, settings);
        // A rotated rectangle is no longer axis aligned, so each bar becomes an explicit quad.
        const barPolygons = pointSets.map((bar) => this.transformLayerPoints(
            [bar[0], bar[1], bar[0] + bar[2], bar[1], bar[0] + bar[2], bar[1] + bar[3], bar[0], bar[1] + bar[3]],
            layerRotation,
            layerScale
        ));

        this.paintCutoutShape(eraseTargets, (graphic) => {
            graphic.beginFill(CUTOUT_ERASE_COLOR, 1);
            barPolygons.forEach((barPolygon) => graphic.drawPolygon(barPolygon));
            graphic.endFill();
        });
    }

    drawCutoutErasePoints(eraseTargets, waveGraphic, settings, points, pointRadius) {
        const { layerRotation, layerScale } = this.prepareCutoutErase(waveGraphic, settings);
        const erasePoints = this.transformLayerPoints(points, layerRotation, layerScale);
        const eraseRadius = pointRadius * layerScale;

        this.paintCutoutShape(eraseTargets, (graphic) => {
            graphic.beginFill(CUTOUT_ERASE_COLOR, 1);
            for (let i = 0; i < erasePoints.length - 1; i += 2) {
                graphic.drawCircle(erasePoints[i], erasePoints[i + 1], eraseRadius);
            }
            graphic.endFill();
        });
    }

    // CUTOUT GLOW BLEED — why a second bloom pass exists.
    //
    // A "Transparent" layer punches a hole through the visualizer, and the global glow is drawn behind
    // the layers, so the hole knocks the glow out along with the fills. The result is a hole with a dead
    // black rim. The glow has to bleed inward from the rim while the hole itself stays a hole.
    //
    // Lifting the shared glow above the knockout does not do that: the bloom keeps the shapes' solid
    // cores, so it simply refills the hole and the mask stops reading as a mask. Instead this pass blooms
    // the same clones with the cutout shapes ERASED out of the source, so the bloom spills into the hole
    // from its rim and nothing solid survives inside it. The pass is then masked to the cutout shapes,
    // which leaves the whole area outside the hole exactly as the regular glow drew it - the wave stays
    // flat under an outer glow, and stays bloomed under an inner one.
    createCutoutGlowBleed(multiplier) {
        this.cutoutGlowMaskGraphic = new PIXI.Graphics();
        this.container.addChild(this.cutoutGlowMaskGraphic);

        this.cutoutGlowEraseGraphic = new PIXI.Graphics();
        this.cutoutGlowEraseGraphic.blendMode = PIXI.BLEND_MODES.ERASE;

        this.cutoutGlowBleedBloomFilter = new PIXIFilters.AdvancedBloomFilter({
            threshold: 0.01,
            quality: 10
        });
        this.cutoutGlowBleedBloomFilter.padding = 100 * multiplier;

        // The nesting below is load-bearing. PixiJS pushes a display object's filters BEFORE its mask,
        // so a container carrying both is masked on the way INTO the bloom: the glow source gets clipped
        // to the hole, where it has just been erased, and the pass renders nothing at all. Keeping the
        // bloom one level below the mask clips the bloom's OUTPUT instead, which is what is wanted.
        this.cutoutGlowBleedContainer = new PIXI.Container();
        this.cutoutGlowBleedContainer.filters = [this.cutoutGlowBleedBloomFilter];

        this.cutoutGlowClipContainer = new PIXI.Container();
        this.cutoutGlowClipContainer.mask = this.cutoutGlowMaskGraphic;
        this.cutoutGlowClipContainer.visible = false;
        this.cutoutGlowClipContainer.addChild(this.cutoutGlowBleedContainer);

        // Added after every layer container so the bleed sits above their ERASE graphics and survives
        // the knockout.
        this.container.addChild(this.cutoutGlowClipContainer);

        this.cutoutGlowBleedActive = false;
    }

    // The glow a layer actually renders with: its own when Customize carries an enabled glow, and the
    // global one otherwise. Null when the layer does not glow at all.
    resolveLayerGlow(settings) {
        const useCustom = settings.customOptions && settings.customOptions.enabled;
        const customGlow = useCustom ? settings.customOptions.glowSettings : null;

        if (customGlow && customGlow.enabled)
            return customGlow;

        return this.config.glowSettings.enabled ? this.config.glowSettings : null;
    }

    // A layer's glow is drawn INSIDE the layer stack when it is per-layer: its own custom glow, or the
    // global one routed per-layer so the clone picks up that layer's fire/shadow distortion. Inside the
    // stack a cutout in front still knocks it out, and the shared glow's route around the knockout does
    // not help it. Those layers are the only ones that still need the bleed pass.
    usesPerLayerGlow(settings) {
        const useCustom = settings.customOptions && settings.customOptions.enabled;
        const hasCustomGlow = useCustom && settings.customOptions.glowSettings && settings.customOptions.glowSettings.enabled;
        const hasCustomFire = useCustom && settings.customOptions.fireSettings && settings.customOptions.fireSettings.enabled;
        const hasCustomShadow = useCustom && settings.customOptions.dropShadowSettings && settings.customOptions.dropShadowSettings.enabled;

        return Boolean(hasCustomGlow || (this.config.glowSettings.enabled && (hasCustomFire || hasCustomShadow)));
    }

    feedsCutoutGlowBleed(settings) {
        return Boolean(settings.visible && !settings.customOptions?.cutout && this.usesPerLayerGlow(settings));
    }

    // One bloom pass serves the whole hole, so it needs a single blur and scale. The pass now exists
    // only for per-layer glows, so its parameters come from the first layer feeding it.
    resolveCutoutGlowBleedGlow() {
        const contributor = this.waveSettings.find((settings) => this.feedsCutoutGlowBleed(settings));
        return contributor ? this.resolveLayerGlow(contributor) : null;
    }

    // Decided before the layer loop so the knockout painters and applyGlowEffect both know whether to
    // feed the bleed pass while they walk the layers. The shared glow no longer needs it: it sits
    // outside the isolated layer stack and its source carries the knockouts, so it already spills into
    // the hole on its own, and running the bleed for it too would light the hole twice.
    prepareCutoutGlowBleed() {
        const hasVisibleCutout = this.waveSettings.some((settings) => settings.visible && settings.customOptions?.cutout);

        this.cutoutGlowBleedActive = hasVisibleCutout
            && this.waveSettings.some((settings) => this.feedsCutoutGlowBleed(settings));

        this.cutoutGlowClipContainer.visible = this.cutoutGlowBleedActive;
        this.cutoutGlowBleedContainer.removeChildren();
        this.cutoutGlowEraseGraphic.clear();
        this.cutoutGlowMaskGraphic.clear();
    }

    finishCutoutGlowBleed() {
        // Re-attached every frame because removeChildren() above detaches it with the clones. It has to
        // be the last child so it knocks the hole out of the whole glow source the bloom is taken from.
        // Re-attached even when the bleed is idle, so the graphic always has an owner to destroy it.
        this.cutoutGlowBleedContainer.addChild(this.cutoutGlowEraseGraphic);
    }

    drawWaveCircle(graphic, settings, points, magnitudePercents, strokeOnly) {
        let mixPercent = average(magnitudePercents) * 5;
        mixPercent = mixPercent > 1 ? 1 : mixPercent;

        graphic.clear();
        graphic.rotation = this.getLayerRotation(settings);

        let color = settings.fillColor;
        let alpha = settings.fillAlpha;
        if (settings.secondaryFillColor) {
            color = (color === '0x000000' ? '0x010101' : color).replace('0x', '#');
            let secondaryColor = (settings.secondaryFillColor === '0x000000' ? '0x010101' : settings.secondaryFillColor).replace('0x', '#');
            color = chroma.mix(color, secondaryColor, mixPercent, 'lch').hex().replace('#', '0x');
            alpha = settings.fillAlpha * (1 - mixPercent) + settings.secondaryFillAlpha * mixPercent;
        }
        let lineColor = settings.lineColor;
        let lineAlpha = settings.lineAlpha;
        if (settings.secondaryLineColor) {
            lineColor = (lineColor === '0x000000' ? '0x010101' : lineColor).replace('0x', '#');
            let secondaryLineColor = (settings.secondaryLineColor === '0x000000' ? '0x010101' : settings.secondaryLineColor).replace('0x', '#');
            lineColor = chroma.mix(lineColor, secondaryLineColor, mixPercent, 'lch').hex().replace('#', '0x');
            lineAlpha = settings.lineAlpha * (1 - mixPercent) + settings.secondaryLineAlpha * mixPercent;
        }
        graphic.lineStyle(settings.lineWidth, lineColor, lineAlpha);
        graphic.beginFill(color, strokeOnly ? 0 : alpha);
        graphic.drawPolygon(points);
        graphic.endFill();
    }

    drawWaveCirclePoints(graphic, settings, points, radius, magnitudePercents) {
        graphic.clear();
        graphic.rotation = this.getLayerRotation(settings);

        for (let i = 0; i < points.length - 1; i += 2) {
            let color = settings.fillColor;
            let alpha = settings.fillAlpha;
            if (settings.secondaryFillColor) {
                color = (color === '0x000000' ? '0x010101' : color).replace('0x', '#');
                let secondaryColor = (settings.secondaryFillColor === '0x000000' ? '0x010101' : settings.secondaryFillColor).replace('0x', '#');
                color = chroma.mix(color, secondaryColor, magnitudePercents[i / 2], 'lch').hex().replace('#', '0x');
                alpha = settings.fillAlpha * (1 - magnitudePercents[i / 2]) + settings.secondaryFillAlpha * magnitudePercents[i / 2];
            }
            let lineColor = settings.lineColor;
            let lineAlpha = settings.lineAlpha;
            if (settings.secondaryLineColor) {
                lineColor = (lineColor === '0x000000' ? '0x010101' : lineColor).replace('0x', '#');
                let secondaryLineColor = (settings.secondaryLineColor === '0x000000' ? '0x010101' : settings.secondaryLineColor).replace('0x', '#');
                lineColor = chroma.mix(lineColor, secondaryLineColor, magnitudePercents[i / 2], 'lch').hex().replace('#', '0x');
                lineAlpha = settings.lineAlpha * (1 - magnitudePercents[i / 2]) + settings.secondaryLineAlpha * magnitudePercents[i / 2];
            }
            graphic.lineStyle(settings.lineWidth, lineColor, lineAlpha);
            graphic.beginFill(color, alpha);
            graphic.drawCircle(points[i], points[i + 1], radius);
            graphic.endFill();
        }

    }

    drawWaveCircleBars(graphic, settings, points, magnitudePercents) {
        graphic.clear();
        graphic.rotation = this.getLayerRotation(settings);

        for (let i = 0; i < points.length; i++) {
            let color = settings.fillColor;
            let alpha = settings.fillAlpha;
            if (settings.secondaryFillColor) {
                color = (color === '0x000000' ? '0x010101' : color).replace('0x', '#');
                let secondaryColor = (settings.secondaryFillColor === '0x000000' ? '0x010101' : settings.secondaryFillColor).replace('0x', '#');
                color = chroma.mix(color, secondaryColor, magnitudePercents[i], 'lch').hex().replace('#', '0x');
                alpha = settings.fillAlpha * (1 - magnitudePercents[i]) + settings.secondaryFillAlpha * magnitudePercents[i];
            }
            let lineColor = settings.lineColor;
            let lineAlpha = settings.lineAlpha;
            if (settings.secondaryLineColor) {
                lineColor = (lineColor === '0x000000' ? '0x010101' : lineColor).replace('0x', '#');
                let secondaryLineColor = (settings.secondaryLineColor === '0x000000' ? '0x010101' : settings.secondaryLineColor).replace('0x', '#');
                lineColor = chroma.mix(lineColor, secondaryLineColor, magnitudePercents[i], 'lch').hex().replace('#', '0x');
                lineAlpha = settings.lineAlpha * (1 - magnitudePercents[i]) + settings.secondaryLineAlpha * magnitudePercents[i];
            }
            graphic.lineStyle(settings.lineWidth, lineColor, lineAlpha);
            graphic.beginFill(color, alpha);
            graphic.drawRect(points[i][0], points[i][1], points[i][2], points[i][3]);
            graphic.endFill();
        }

    }

    spectrumToPoints(spectrum, width, baseHeight, reflection, inverted, waveScale, targetMax, waveStyle) {
        let points = [];
        let magnitudePercents = [];
        let length = spectrum.length;

        let newSpectrum = inverted ? spectrum.slice().reverse() : spectrum.slice();

        for (let i = 0; i < length; i++) {
            let x;
            if (reflection === REFLECTION_TYPE.ONE_SIDE || reflection === REFLECTION_TYPE.COMBO)
                x = i * ((width / 2) / (length - 1)) - (width / 2);
            else {
                x = i * (width / (length - 1)) - width / 2;
            }
            const targetIndex = i * 2;
            points[targetIndex] = x;
            points[targetIndex + 1] = -(newSpectrum[i] * waveScale + baseHeight);

            const magnitudePercent = newSpectrum[i] / targetMax;
            magnitudePercents[i] = magnitudePercent > 1 ? 1 : magnitudePercent;

            let targetIndex2;
            switch (reflection) {
                case REFLECTION_TYPE.NONE:
                    break;
                case REFLECTION_TYPE.ONE_SIDE:
                    //don't add last point multiple times
                    if (i === length - 1)
                        continue;

                    targetIndex2 = length * 4 - (i * 2 + 4);
                    points[targetIndex2] = -points[targetIndex];
                    points[targetIndex2 + 1] = points[targetIndex + 1];

                    magnitudePercents[length * 2 - i - 2] = magnitudePercents[i];
                    break;
                case REFLECTION_TYPE.TWO_SIDE:
                    targetIndex2 = length * 4 - (i * 2 + 2);
                    points[targetIndex2] = points[targetIndex];
                    points[targetIndex2 + 1] = -points[targetIndex + 1];

                    magnitudePercents[length * 2 - i - 1] = magnitudePercents[i];
                    break;
                case REFLECTION_TYPE.COMBO:
                    //don't add last point multiple times
                    if (i !== length - 1) {
                        targetIndex2 = length * 4 - (i * 2 + 4);
                        points[targetIndex2] = -points[targetIndex];
                        points[targetIndex2 + 1] = points[targetIndex + 1];

                        targetIndex2 = length * 4 + (i * 2 - 2);
                        points[targetIndex2] = -points[targetIndex];
                        points[targetIndex2 + 1] = -points[targetIndex + 1];

                        magnitudePercents[length * 2 - i - 2] = magnitudePercents[i];
                        magnitudePercents[length * 2 + i - 1] = magnitudePercents[i];
                    }

                    targetIndex2 = length * 8 - (i * 2 + 6);
                    points[targetIndex2] = points[targetIndex];
                    points[targetIndex2 + 1] = -points[targetIndex + 1];

                    magnitudePercents[length * 4 - i - 3] = magnitudePercents[i];
                    break;
                default:
                    break;
            }
        }

        if (waveStyle === WAVE_STYLE.SOLID && (reflection === REFLECTION_TYPE.NONE || reflection === REFLECTION_TYPE.ONE_SIDE)) {
            const extraPoints = [points[0], 0, points[points.length - 2], 0];
            points.unshift(extraPoints[1]);
            points.unshift(extraPoints[0]);
            points.push(extraPoints[2]);
            points.push(extraPoints[3]);

            magnitudePercents.unshift(0);
            magnitudePercents.push(0);
        }

        return { points: points, magnitudePercents: magnitudePercents };
    }

    spectrumToBarPoints(spectrum, width, baseHeight, reflection, inverted, waveScale, barWidth, targetMax) {
        let pointSets = [];
        let magnitudePercents = [];
        let length = spectrum.length;

        let newSpectrum = inverted ? spectrum.slice().reverse() : spectrum.slice();

        let calculatedBarWidth = (width / length) * barWidth;
        if (reflection === REFLECTION_TYPE.ONE_SIDE || reflection === REFLECTION_TYPE.COMBO)
            calculatedBarWidth = (width / (length - 0.5)) * barWidth / 2;
        for (let i = 0; i < length; i++) {
            let x;
            if (reflection === REFLECTION_TYPE.ONE_SIDE || reflection === REFLECTION_TYPE.COMBO) {
                const barContainerWidth = (width / 2) / (length - 0.5);
                const shift = (barContainerWidth - calculatedBarWidth) / 2;
                x = i * barContainerWidth - (width / 2) + shift;
            }
            else {
                const shift = ((width / length) - calculatedBarWidth) / 2;
                x = i * (width / length) - (width / 2) + shift;
            }

            pointSets[i] = [x, -(newSpectrum[i] * waveScale + baseHeight), calculatedBarWidth];

            const magnitudePercent = newSpectrum[i] / targetMax;
            magnitudePercents[i] = magnitudePercent > 1 ? 1 : magnitudePercent;

            let targetIndex2;
            switch (reflection) {
                case REFLECTION_TYPE.NONE:
                    pointSets[i][3] = -pointSets[i][1];
                    break;
                case REFLECTION_TYPE.ONE_SIDE:
                    pointSets[i][3] = -pointSets[i][1];

                    targetIndex2 = length * 2 - i - 1;
                    pointSets[targetIndex2] = [-pointSets[i][0] - calculatedBarWidth, pointSets[i][1], pointSets[i][2], pointSets[i][3]];

                    magnitudePercents[targetIndex2] = magnitudePercents[i];
                    break;
                case REFLECTION_TYPE.TWO_SIDE:
                    pointSets[i][3] = -pointSets[i][1] * 2;
                    break;
                case REFLECTION_TYPE.COMBO:
                    pointSets[i][3] = -pointSets[i][1] * 2;

                    targetIndex2 = length * 2 - i - 1;
                    pointSets[targetIndex2] = [-pointSets[i][0] - calculatedBarWidth, pointSets[i][1], pointSets[i][2], pointSets[i][3]];

                    magnitudePercents[targetIndex2] = magnitudePercents[i];
                    break;
                default:
                    break;
            }
        }

        return { pointSets: pointSets, magnitudePercents: magnitudePercents };
    }



    setPositionAndScale(multiplier) {
        this.baseScaleMultiplier = multiplier;
        this.container.scale.set(multiplier, multiplier);

        this.moveTo(this.config.position);
    }

    // Separate from setPositionAndScale so a canvas drag can move the visualizer on its own, without a
    // round trip through the store, and still land on the same pixel the stored position means. Bounce,
    // shake and drift all offset from this.x and this.y rather than replacing them, so they keep
    // working from the new place without being told anything.
    moveTo(position) {
        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;

        this.x = CONSTANTS.DEFAULT_WIDTH * 0.5 + (position.x * multiplier);
        this.y = CONSTANTS.DEFAULT_HEIGHT * 0.5 + (position.y * multiplier);
        this.container.x = this.x;
        this.container.y = this.y;
    }

    updateStage(stage) {
        this.stage = stage;
        this.stage.addChild(this.container);
    }

    initializeLayer(waveCircles) {
        this.waveCircles = [];
        this.layerEffects = [];
        this.container.removeChildren();

        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;

        // Padding covers the glow extent so the isolating filter does not clip the glow while active.
        this.cutoutIsolationFilter.padding = 200 * multiplier;
        this.layerStackIsolationFilter.padding = 200 * multiplier;
        // Children were just rebuilt; drop any leftover cutout isolation from a previous layer set.
        this.container.filters = null;

        // SHARED GLOW CONTAINER:
        // We create a shared container for global glow *before* adding individual layer containers.
        // Why? In the original architecture, all layer clones went into one shared container
        // with one bloom filter. This created a combined glow where layer colors mix naturally.
        // If we put global glow on each layer individually, their blooms don't mix correctly and
        // the glow appears as an "inner" glow because it gets trapped under each wave layer.
        this.sharedGlowContainer = new PIXI.Container();
        this.sharedGlowContainer.filters = [];
        this.container.addChild(this.sharedGlowContainer);

        // Every layer container lives in here rather than directly in the visualizer container, so the
        // stack renders into its own framebuffer and a cutout's ERASE cannot reach the glow beneath it.
        this.layerStackContainer = new PIXI.Container();
        this.container.addChild(this.layerStackContainer);

        // Add shared bloom filter
        this.sharedBloomFilter = new PIXIFilters.AdvancedBloomFilter({
            threshold: 0.01,
            quality: 10
        });
        this.sharedBloomFilter.padding = 100 * multiplier;
        this.sharedGlowContainer.filters.push(this.sharedBloomFilter);

        // Add shared shadow filters for when global glow + shadow are both active
        const shadowSettings = { quality: 10, distance: 0 };
        this.sharedGlowShadowFilter1 = new PIXIFilters.DropShadowFilter(shadowSettings);
        this.sharedGlowShadowFilter2 = new PIXIFilters.DropShadowFilter(shadowSettings);
        this.sharedGlowContainer.filters.push(this.sharedGlowShadowFilter1);
        this.sharedGlowContainer.filters.push(this.sharedGlowShadowFilter2);

        // Add shared fire displacement filter for global glow + fire combination
        this.sharedDisplacementSprite = new PIXI.Sprite();
        this.sharedDisplacementSprite.texture = PIXI.Texture.from('https://specterr.b-cdn.net/fire-texture.jpg');
        this.sharedDisplacementSprite.texture.baseTexture.wrapMode = PIXI.WRAP_MODES.MIRRORED_REPEAT;
        this.sharedDisplacementFilter = new PIXI.filters.DisplacementFilter(this.sharedDisplacementSprite);
        this.sharedDisplacementSprite.x -= 500;
        this.sharedDisplacementFilter.padding = 110 * multiplier;
        this.sharedDisplacementFilter.enabled = false;
        // Add sprite to container (needed for filter to work)
        this.container.addChild(this.sharedDisplacementSprite);
        // Insert displacement filter before bloom filter
        this.sharedGlowContainer.filters.unshift(this.sharedDisplacementFilter);

        waveCircles.forEach((settings, index) => this.addNewLayer(settings, index, multiplier));

        this.createCutoutGlowBleed(multiplier);
    }

    addNewLayer(settings, index, multiplier) {
        if (settings.frameDelay > 0)
            this.maxFrameDelay = Math.max(settings.frameDelay, this.maxFrameDelay);

        const layerContainer = new PIXI.Container();
        const waveContainer = new PIXI.Container();
        const glowContainer = new PIXI.Container();

        waveContainer.filters = [];
        glowContainer.filters = [];

        layerContainer.addChild(glowContainer);
        layerContainer.addChild(waveContainer);
        this.layerStackContainer.addChild(layerContainer);

        const waveCircle = new PIXI.Graphics();
        waveCircle.scale.set(settings.scale, settings.scale);
        waveContainer.addChild(waveCircle);

        // The glow source mirrors the layer stack one slot per layer, each with its own knockout at the
        // same z-position, so the bloom is computed from the colour that is actually seen rather than
        // from shapes the hole covers.
        const glowSlotContainer = new PIXI.Container();
        const glowWaveHolder = new PIXI.Container();
        const glowEraseGraphic = new PIXI.Graphics();
        glowEraseGraphic.blendMode = PIXI.BLEND_MODES.ERASE;
        glowSlotContainer.addChild(glowWaveHolder);
        glowSlotContainer.addChild(glowEraseGraphic);
        this.sharedGlowContainer.addChild(glowSlotContainer);

        // Per-layer ERASE knockout for "Transparent". Attached as the TOP child of the layer container
        // below (after the effect-setup calls, which also add children), so it sits at the layer's
        // z-position: ERASE removes only what was drawn before it (the layers BEHIND this one), while
        // later layers draw on top. Stays empty unless this layer is a cutout (filled per frame in draw).
        const cutoutEraseGraphic = new PIXI.Graphics();
        cutoutEraseGraphic.blendMode = PIXI.BLEND_MODES.ERASE;

        const effects = {
            layerContainer,
            waveContainer,
            glowContainer,
            cutoutEraseGraphic,
            glowWaveHolder,
            glowEraseGraphic,
            // Every knockout shape is painted into both, so the layer stack and the glow source always
            // agree on where the holes are.
            cutoutEraseTargets: [cutoutEraseGraphic, glowEraseGraphic],
            filters: []
        };

        this.addLayerGlowEffect(effects, multiplier);
        this.addLayerFireEffect(effects, multiplier);
        this.addLayerDropShadowEffect(effects, multiplier);

        // Attach the ERASE graphic LAST so it is the top child, above every child added during effect
        // setup (e.g. the fire displacement sprite), keeping a cutout's hole clean through the layers behind.
        layerContainer.addChild(cutoutEraseGraphic);

        this.waveCircles.push(waveCircle);
        this.layerEffects.push(effects);
    }

    addLayerGlowEffect(effects, multiplier) {
        effects.bloomFilter = new PIXIFilters.AdvancedBloomFilter({
            threshold: 0.01,
            quality: 10
        });
        effects.glowContainer.filters.push(effects.bloomFilter);
        effects.bloomFilter.padding = 100 * multiplier;
    }

    addLayerFireEffect(effects, multiplier) {
        effects.displacementSprite = new PIXI.Sprite();
        effects.displacementSprite.texture = PIXI.Texture.from('https://specterr.b-cdn.net/fire-texture.jpg');
        effects.displacementSprite.texture.baseTexture.wrapMode = PIXI.WRAP_MODES.MIRRORED_REPEAT;
        effects.displacementFilter = new PIXI.filters.DisplacementFilter(effects.displacementSprite);
        effects.displacementSprite.x -= 500;
        effects.displacementFilter.padding = 110 * multiplier;

        effects.displacementFilter.enabled = false;

        effects.layerContainer.addChild(effects.displacementSprite);
        effects.glowContainer.filters.push(effects.displacementFilter);
        effects.waveContainer.filters.push(effects.displacementFilter);
    }

    addLayerDropShadowEffect(effects, multiplier) {
        const shadowSettings = {
            quality: 10,
            distance: 0,
        };
        effects.glowShadowFilter1 = new PIXIFilters.DropShadowFilter(shadowSettings);
        effects.glowShadowFilter2 = new PIXIFilters.DropShadowFilter(shadowSettings);
        effects.waveShadowFilter1 = new PIXIFilters.DropShadowFilter(shadowSettings);
        effects.waveShadowFilter2 = new PIXIFilters.DropShadowFilter(shadowSettings);

        effects.waveShadowPaddingFilter = new PIXI.filters.AlphaFilter(1);
        effects.waveShadowPaddingFilter.padding = 100 * multiplier;

        effects.glowContainer.filters.push(effects.glowShadowFilter1);
        effects.glowContainer.filters.push(effects.glowShadowFilter2);
        effects.waveContainer.filters.push(effects.waveShadowFilter1);
        effects.waveContainer.filters.push(effects.waveShadowFilter2);
        effects.waveContainer.filters.push(effects.waveShadowPaddingFilter);
    }

    updateLayerEffects(multiplier) {
        // Guard: skip if config hasn't been populated yet (updateLayers can be called before update)
        if (!this.config.glowSettings || !this.config.dropShadowSettings || !this.config.fireSettings) return;

        const globalGlow = this.config.glowSettings;
        const globalShadow = this.config.dropShadowSettings;

        // Update shared bloom filter (for global glow)
        if (this.sharedBloomFilter) {
            this.sharedBloomFilter.blur = globalGlow.blur * multiplier;
            this.sharedBloomFilter.bloomScale = globalGlow.scale;
            this.sharedBloomFilter.enabled = globalGlow.enabled;
        }

        // The bleed into a transparency cutout is the same light as the layers around the hole, so it
        // follows whichever glow lights them. Its container is hidden outright when there is nothing to
        // bleed into, so the filter stays enabled.
        const cutoutBleedGlow = this.resolveCutoutGlowBleedGlow();
        if (this.cutoutGlowBleedBloomFilter && cutoutBleedGlow) {
            this.cutoutGlowBleedBloomFilter.blur = cutoutBleedGlow.blur * multiplier;
            this.cutoutGlowBleedBloomFilter.bloomScale = cutoutBleedGlow.scale;
        }

        // Update shared shadow filters (for when global glow + shadow are both active)
        // Update shared shadow filters (with null checks)
        if (this.sharedGlowShadowFilter1 && this.sharedGlowShadowFilter2) {
            // The global shadow belongs on the shared source whether or not the global glow is on.
            // Gating it on the glow used to drop every layer into the per-layer branch, and a per-layer
            // shadow renders inside its own layer's node, so the front layer's shadow landed on top of
            // the layers behind it.
            const sharedShadowEnabled = globalShadow.enabled;
            this.sharedGlowShadowFilter1.enabled = sharedShadowEnabled;
            this.sharedGlowShadowFilter2.enabled = sharedShadowEnabled;

            // Without a bloom in front of it the shared source would draw a second, opaque copy of the
            // whole stack underneath the real one: visible through any transparency cutout, and doubling
            // any layer with alpha below 1. shadowOnly drops that copy from the output and keeps the
            // shadow. It goes on the SECOND pass only: the two passes exist to deepen the shadow, which
            // works while the first pass still has the original for the second one to darken.
            this.sharedGlowShadowFilter2.shadowOnly = !globalGlow.enabled;
            this.sharedGlowShadowFilter1.blur = globalShadow.blur * multiplier;
            this.sharedGlowShadowFilter2.blur = globalShadow.blur * multiplier;

            // PixiJS sizes ONE framebuffer for the whole chain, from the largest padding among the
            // ENABLED filters, and DropShadowFilter pads itself to `distance + blur * 2`, enough for a
            // single pass. These two are chained, so the second blurs the output of the first and the
            // shadow travels about twice as far as that. With the glow on, the bloom's own padding was
            // large enough to hide the shortfall; with it off the shadow ended on a straight
            // rectangular edge. Padding is assigned AFTER blur because the blur setter recomputes it.
            const sharedShadowPadding = Math.max(100 * multiplier, globalShadow.blur * multiplier * 4);
            this.sharedGlowShadowFilter1.padding = sharedShadowPadding;
            this.sharedGlowShadowFilter2.padding = sharedShadowPadding;
            this.sharedGlowShadowFilter1.alpha = globalShadow.opacity;
            this.sharedGlowShadowFilter2.alpha = globalShadow.opacity;
            this.sharedGlowShadowFilter1.color = globalShadow.color;
            this.sharedGlowShadowFilter2.color = globalShadow.color;
        }

        // Update shared fire filter for global glow + fire combination
        if (this.sharedDisplacementFilter) {
            const sharedFireEnabled = globalGlow.enabled && this.config.fireSettings.enabled;
            this.sharedDisplacementFilter.enabled = sharedFireEnabled;
            if (sharedFireEnabled) {
                const displacementScale = this.config.fireSettings.intensity * multiplier;
                this.sharedDisplacementFilter.scale = new PIXI.Point(displacementScale, displacementScale * 10);
            }
            if (this.sharedDisplacementSprite) {
                this.sharedDisplacementSprite.scale.y = this.config.fireSettings.detail;
                this.sharedDisplacementSprite.scale.x = this.config.fireSettings.detail;
            }
        }

        this.layerEffects.forEach((effects, index) => {
            const settings = this.waveSettings[index];
            if (!settings) return;
            const useCustom = settings.customOptions && settings.customOptions.enabled;

            // Determine if this layer has custom per-layer glow
            const hasCustomGlow = useCustom && settings.customOptions?.glowSettings && settings.customOptions.glowSettings.enabled;
            const hasCustomFire = useCustom && settings.customOptions?.fireSettings && settings.customOptions.fireSettings.enabled;
            const hasCustomShadow = useCustom && settings.customOptions?.dropShadowSettings && settings.customOptions.dropShadowSettings.enabled;
            const layerGlow = hasCustomGlow ? settings.customOptions.glowSettings : null;

            // Per-layer bloom: enabled for custom per-layer glow, OR when
            // global glow is active and the layer has custom fire or custom shadow.
            const usePerLayerBloom = hasCustomGlow || (globalGlow.enabled && (hasCustomFire || hasCustomShadow));
            if (usePerLayerBloom && effects.bloomFilter) {
                const glowSource = layerGlow || globalGlow;
                effects.bloomFilter.blur = glowSource.blur * multiplier;
                effects.bloomFilter.bloomScale = glowSource.scale;
                effects.bloomFilter.enabled = true;

                if (glowSource.glowType === GLOW_TYPE.INNER) {
                    effects.waveContainer.alpha = 0;
                } else {
                    effects.waveContainer.alpha = 1;
                }
            } else if (effects.bloomFilter) {
                effects.bloomFilter.enabled = false;

                // Global glow controls waveContainer alpha for inner/outer
                if (globalGlow.enabled && globalGlow.glowType === GLOW_TYPE.INNER) {
                    effects.waveContainer.alpha = 0;
                } else {
                    effects.waveContainer.alpha = 1;
                }
            }

            // Fire
            const fire = useCustom && settings.customOptions?.fireSettings && settings.customOptions.fireSettings.enabled ? settings.customOptions.fireSettings : this.config.fireSettings;
            if (effects.displacementFilter && effects.displacementSprite) {
                effects.displacementFilter.enabled = fire.enabled;
                const displacementScale = fire.enabled ? fire.intensity * multiplier : 0;
                effects.displacementFilter.scale = new PIXI.Point(displacementScale, displacementScale * 10);
                effects.displacementSprite.scale.y = fire.detail;
                effects.displacementSprite.scale.x = fire.detail;
            }

            // Shadow
            const shadow = useCustom && settings.customOptions?.dropShadowSettings && settings.customOptions.dropShadowSettings.enabled ? settings.customOptions.dropShadowSettings : this.config.dropShadowSettings;
            // Routing a shadow onto the glowContainer is how a layer declines to cast its own. For a
            // layer with a per-layer bloom that container holds its clone; for a layer served by the
            // shared source it is empty, so the only shadow drawn is the single one beneath the whole
            // stack. A layer with its OWN custom shadow stays on the per-layer route, because that
            // shadow deliberately belongs to that layer.
            const shadowRidesOnGlowContainer = usePerLayerBloom || !hasCustomShadow;
            const glowShadowEnabled = shadowRidesOnGlowContainer && shadow.enabled;
            const waveShadowEnabled = !shadowRidesOnGlowContainer && shadow.enabled;

            if (effects.glowShadowFilter1 && effects.glowShadowFilter2) {
                effects.glowShadowFilter1.enabled = glowShadowEnabled;
                effects.glowShadowFilter2.enabled = glowShadowEnabled;
                effects.glowShadowFilter1.blur = shadow.blur * multiplier;
                effects.glowShadowFilter2.blur = shadow.blur * multiplier;
                effects.glowShadowFilter1.alpha = shadow.opacity;
                effects.glowShadowFilter2.alpha = shadow.opacity;
                effects.glowShadowFilter1.color = shadow.color;
                effects.glowShadowFilter2.color = shadow.color;
            }

            if (effects.waveShadowFilter1 && effects.waveShadowFilter2) {
                effects.waveShadowFilter1.enabled = waveShadowEnabled;
                effects.waveShadowFilter2.enabled = waveShadowEnabled;
                effects.waveShadowFilter1.blur = shadow.blur * multiplier;
                effects.waveShadowFilter2.blur = shadow.blur * multiplier;
                effects.waveShadowFilter1.alpha = shadow.opacity;
                effects.waveShadowFilter2.alpha = shadow.opacity;
                effects.waveShadowFilter1.color = shadow.color;
                effects.waveShadowFilter2.color = shadow.color;
            }

            if (effects.waveShadowPaddingFilter) {
                effects.waveShadowPaddingFilter.enabled = waveShadowEnabled;
            }
        });
    }

    getLayerRotation(settings) {
        return settings.customOptions.enabled ? settings.customOptions.rotation * (Math.PI / 180) : 0;
    }

    getVisualizationOptions(settings) {
        const { customOptions } = settings;
        const useCustom = customOptions && customOptions.enabled;

        return {
            waveType: useCustom ? customOptions.waveType : this.config.waveType,
            barWidth: useCustom ? customOptions.barWidth : this.config.barWidth,
            pointRadius: useCustom ? customOptions.pointRadius : this.config.pointRadius,
            waveStyle: useCustom ? customOptions.waveStyle : this.config.waveStyle,
            inverted: useCustom ? customOptions.inverted : this.config.inverted,
            reflection: useCustom ? customOptions.reflectionType : this.config.reflection,
            // Transparent is a layer-level toggle, so it applies even when Customize is off.
            cutout: customOptions && customOptions.cutout !== undefined ? customOptions.cutout : false
        };
    }

    destroy() {
        this.container.destroy({ children: true });
    }
}